import { parseDecimal, toInputDecimal } from "@/lib/parseDecimal";
import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Pencil, Plus, Search, Trash2 } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";
import { HourTotals, emptyTotals, fmtHours, loadOrderHourTotals, sumTotals } from "@/lib/lnwHours";
import { canCloseProject, dbErrorMessage } from "@/lib/closeRules";
import { ArchiveSection, ClosedBadge } from "@/components/admin/ArchiveSection";

type Project = Tables<"projects">;
type Customer = Tables<"customers">;
type Contact = Tables<"customer_contacts">;
type Order = Tables<"orders">;
type ProjectRow = Project & { customers: { company: string } | null };

const AdminProjects = () => {
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [orderTotals, setOrderTotals] = useState<Record<string, HourTotals>>({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);
  const [form, setForm] = useState({ customer_id: "", contact_id: "none", name: "", order_reference: "", hourly_rate: "" });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [archiveOpen, setArchiveOpen] = useState(false);

  const load = async () => {
    const [{ data: p, error: pe }, { data: c, error: ce }, { data: cc }, { data: o }, totals] = await Promise.all([
      supabase.from("projects").select("*, customers(company)").order("created_at", { ascending: false }),
      supabase.from("customers").select("*").order("company"),
      supabase.from("customer_contacts").select("*").order("name"),
      supabase.from("orders").select("*"),
      loadOrderHourTotals(),
    ]);
    if (pe || ce) toast.error("Daten konnten nicht geladen werden");
    setProjects((p ?? []) as unknown as ProjectRow[]);
    setCustomers(c ?? []);
    setContacts(cc ?? []);
    setOrders(o ?? []);
    setOrderTotals(totals);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const projectSummary = useMemo(() => {
    const map: Record<string, { hours: HourTotals; budget: number | null; orderCount: number }> = {};
    for (const project of projects) {
      const projectOrders = orders.filter((o) => o.project_id === project.id);
      const hours = sumTotals(projectOrders.map((o) => orderTotals[o.id] ?? emptyTotals()));
      const budgetValues = projectOrders.map((o) => (o.budget_hours != null ? Number(o.budget_hours) : null));
      const budget = budgetValues.some((v) => v != null)
        ? budgetValues.reduce((acc: number, v) => acc + (v ?? 0), 0)
        : null;
      map[project.id] = { hours, budget, orderCount: projectOrders.length };
    }
    return map;
  }, [projects, orders, orderTotals]);

  const contactsForForm = contacts.filter((c) => c.customer_id === form.customer_id);

  const openNew = () => {
    setEditing(null);
    setForm({ customer_id: customers[0]?.id ?? "", contact_id: "none", name: "", order_reference: "", hourly_rate: "" });
    setDialogOpen(true);
  };

  const openEdit = (p: ProjectRow) => {
    setEditing(p);
    setForm({
      customer_id: p.customer_id,
      contact_id: p.contact_id ?? "none",
      name: p.name,
      order_reference: p.order_reference ?? "",
      hourly_rate: toInputDecimal(p.hourly_rate),
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.customer_id || !form.name.trim()) {
      toast.error("Bitte wählen Sie einen Kunden und geben Sie einen Projektnamen an.");
      return;
    }
    const rate = form.hourly_rate.trim() ? parseDecimal(form.hourly_rate) : null;
    if (form.hourly_rate.trim() && (rate == null || Number.isNaN(rate) || rate < 0)) {
      toast.error("Stundensatz ist ungültig.");
      return;
    }
    setSaving(true);
    const payload = {
      customer_id: form.customer_id,
      contact_id: form.contact_id === "none" ? null : form.contact_id,
      name: form.name.trim(),
      order_reference: form.order_reference.trim() || null,
      hourly_rate: rate,
    };
    const { error } = editing
      ? await supabase.from("projects").update(payload).eq("id", editing.id)
      : await supabase.from("projects").insert(payload);
    setSaving(false);
    if (error) {
      toast.error("Speichern fehlgeschlagen");
      return;
    }
    toast.success("Projekt gespeichert");
    setDialogOpen(false);
    load();
  };

  const remove = async (p: ProjectRow) => {
    if (!window.confirm(`Projekt „${p.name}" wirklich löschen? Zugehörige Aufträge und Leistungsnachweise werden ebenfalls gelöscht.`)) return;
    const { error } = await supabase.from("projects").delete().eq("id", p.id);
    if (error) toast.error(dbErrorMessage(error, "Löschen fehlgeschlagen"));
    else {
      toast.success("Projekt gelöscht");
      load();
    }
  };

  const toggleClosed = async (p: ProjectRow) => {
    const closing = !p.closed_at;
    if (closing && !window.confirm(`Projekt „${p.name}" abschließen und archivieren?`)) return;
    const { error } = await supabase.from("projects").update({ closed_at: closing ? new Date().toISOString() : null }).eq("id", p.id);
    if (error) toast.error(dbErrorMessage(error, "Aktion fehlgeschlagen"));
    else {
      toast.success(closing ? "Projekt abgeschlossen" : "Projekt wieder geöffnet");
      load();
    }
  };

  const term = search.trim().toLowerCase();
  const matches = (p: ProjectRow) =>
    !term || [p.name, p.order_reference ?? "", p.customers?.company ?? ""].join(" ").toLowerCase().includes(term);
  const activeProjects = projects.filter((p) => !p.closed_at && matches(p));
  const closedProjects = projects.filter((p) => !!p.closed_at && matches(p));
  const showArchive = archiveOpen || (!!term && closedProjects.length > 0);

  const header = (
    <TableHeader>
      <TableRow>
        <TableHead>Projekt</TableHead>
        <TableHead className="hidden md:table-cell">Kunde</TableHead>
        <TableHead className="hidden lg:table-cell">Ansprechpartner</TableHead>
        <TableHead className="text-right">Aufträge</TableHead>
        <TableHead className="text-right">Kontingent</TableHead>
        <TableHead className="text-right">Genehmigt</TableHead>
        <TableHead className="text-right">Offen</TableHead>
        <TableHead className="w-32 text-right">Aktionen</TableHead>
      </TableRow>
    </TableHeader>
  );

  const renderRow = (p: ProjectRow) => {
    const summary = projectSummary[p.id] ?? { hours: emptyTotals(), budget: null, orderCount: 0 };
    const contact = contacts.find((c) => c.id === p.contact_id);
    const check = canCloseProject(orders.filter((o) => o.project_id === p.id));
    return (
      <TableRow key={p.id} className={p.closed_at ? "text-muted-foreground" : undefined}>
        <TableCell>
          <div className="font-medium">{p.name}</div>
          <div className="text-xs text-muted-foreground">{p.order_reference ?? "ohne Auftragskennung"}</div>
          {p.closed_at && <ClosedBadge closedAt={p.closed_at} />}
        </TableCell>
        <TableCell className="hidden md:table-cell">{p.customers?.company ?? "–"}</TableCell>
        <TableCell className="hidden lg:table-cell">{contact?.name ?? "–"}</TableCell>
        <TableCell className="text-right">{summary.orderCount}</TableCell>
        <TableCell className="text-right">{summary.budget != null ? `${fmtHours(summary.budget)} h` : "–"}</TableCell>
        <TableCell className="text-right">{fmtHours(summary.hours.approved)} h</TableCell>
        <TableCell className="text-right text-muted-foreground">{fmtHours(summary.hours.open)} h</TableCell>
        <TableCell className="text-right whitespace-nowrap">
          {p.closed_at ? (
            <Button variant="ghost" size="icon" onClick={() => toggleClosed(p)} aria-label="Wieder öffnen" title="Wieder öffnen">
              <ArchiveRestore className="h-4 w-4" />
            </Button>
          ) : (
            <>
              <span title={check.ok ? "Abschließen" : check.reason}>
                <Button variant="ghost" size="icon" onClick={() => toggleClosed(p)} disabled={!check.ok} aria-label="Abschließen">
                  <Archive className="h-4 w-4" />
                </Button>
              </span>
              <Button variant="ghost" size="icon" onClick={() => openEdit(p)} aria-label="Bearbeiten">
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => remove(p)} aria-label="Löschen">
                <Trash2 className="h-4 w-4" />
              </Button>
            </>
          )}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <Helmet>
        <title>Projekte | Leistungsnachweise</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Projekte</h1>
        <Button className="gap-2" onClick={openNew} disabled={customers.length === 0}>
          <Plus className="h-4 w-4" /> Neues Projekt
        </Button>
      </div>

      {customers.length === 0 && !loading && (
        <p className="mb-4 text-sm text-muted-foreground">Legen Sie zuerst einen Kunden an, bevor Sie Projekte erstellen.</p>
      )}

      <div className="relative mb-4 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Suche nach Projekt, Kennung oder Kunde" className="pl-9" aria-label="Projekte durchsuchen" />
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            {header}
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="py-8 text-center text-muted-foreground">Laden…</TableCell></TableRow>
              ) : activeProjects.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="py-8 text-center text-muted-foreground">{term ? "Keine aktiven Treffer." : "Keine aktiven Projekte."}</TableCell></TableRow>
              ) : (
                activeProjects.map(renderRow)
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {!loading && closedProjects.length > 0 && (
        <ArchiveSection count={closedProjects.length} open={showArchive} onOpenChange={setArchiveOpen}>
          <Table>
            {header}
            <TableBody>{closedProjects.map(renderRow)}</TableBody>
          </Table>
        </ArchiveSection>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Projekt bearbeiten" : "Neues Projekt"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Kunde *</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v, contact_id: "none" })}>
                <SelectTrigger><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.company}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Ansprechpartner (genau einer je Projekt)</Label>
              <Select value={form.contact_id} onValueChange={(v) => setForm({ ...form, contact_id: v })} disabled={!form.customer_id}>
                <SelectTrigger><SelectValue placeholder="Ansprechpartner wählen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kein Ansprechpartner</SelectItem>
                  {contactsForForm.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}{c.email ? ` · ${c.email}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Ansprechpartner pflegen Sie unter „Kunden“. Neue Aufträge übernehmen diesen Ansprechpartner.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pname">Projektname *</Label>
              <Input id="pname" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="oref">Projektkennung (z. B. 10.13258.031. OIZ / TECHnet)</Label>
              <Input id="oref" value={form.order_reference} onChange={(e) => setForm({ ...form, order_reference: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rate">Standard-Stundensatz (€ netto, Vorschlag für neue Aufträge)</Label>
              <Input id="rate" inputMode="decimal" placeholder="z. B. 145" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={save} disabled={saving}>{saving ? "Speichern…" : "Speichern"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminProjects;
